import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Slider,
  Tooltip,
  Divider,
  Alert,
  Grid,
  Button,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import { getSimilarFiles } from "../../services/apiService";
import FileStatusChip from "./shared/FileStatusChip";

const POPPINS = { fontFamily: "'Poppins', sans-serif" };
const HEADER_COLOR = "#1B2559";
const PRIMARY_COLOR = "#7b61ff";

/**
 * Dialog showing semantically similar files (cross-user) for a selected file.
 *
 * @param {{
 *   open: boolean,
 *   file: object|null,
 *   onClose: () => void,
 *   onUseInChat: (file: object) => void,
 * }} props
 */
const SimilarFilesDialog = ({ open, file, onClose, onUseInChat }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [minScore, setMinScore] = useState(0.80);
  const [topK] = useState(10);

  useEffect(() => {
    if (!open || !file?.item_id) return;
    setLoading(true);
    setError(null);
    setResults([]);
    setStatus(null);

    getSimilarFiles(file.item_id, topK, minScore)
      .then((res) => {
        if (res?.status === "not_indexed") {
          setStatus("not_indexed");
          return;
        }
        if (res?.error || res?.status === "error") {
          setError(res.message || "Failed to fetch similar files.");
          return;
        }
        setResults(res?.similar_files ?? []);
        setStatus("ok");
      })
      .catch(() => setError("An unexpected error occurred."))
      .finally(() => setLoading(false));
  }, [open, file?.item_id, minScore, topK]);

  const scoreColor = (score) => {
    if (score >= 0.9) return "#4caf50";
    if (score >= 0.8) return "#ff9800";
    return "#9e9e9e";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "80vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: PRIMARY_COLOR }} />
          <Typography
            variant="subtitle1"
            sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR }}
          >
            Similar Files
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Source file */}
      {file && (
        <Box
          sx={{
            mx: 3,
            mb: 1,
            px: 2,
            py: 1,
            borderRadius: 1,
            backgroundColor: "#f0f4ff",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <InsertDriveFileOutlinedIcon
            sx={{ color: "#1976d2", fontSize: 18, flexShrink: 0 }}
          />
          <Typography
            variant="body2"
            sx={{
              ...POPPINS,
              fontWeight: 600,
              color: "#1976d2",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={file.name}
          >
            {file.name}
          </Typography>
        </Box>
      )}

      {/* Min score slider */}
      <Box sx={{ px: 3, pt: 1 }}>
        <Typography variant="caption" sx={{ ...POPPINS, color: "#555" }}>
          Minimum similarity: <b>{(minScore * 100).toFixed(0)}%</b>
        </Typography>
        <Slider
          value={minScore}
          min={0.5}
          max={1.0}
          step={0.05}
          onChange={(_, v) => setMinScore(v)}
          size="small"
          sx={{ color: PRIMARY_COLOR, mt: 0.5 }}
        />
      </Box>

      <Divider sx={{ mx: 3, mb: 1 }} />

      <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Grid container spacing={2}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && status === "not_indexed" && (
          <Alert severity="info" sx={POPPINS}>
            This file has not been indexed yet. Similar files will be available
            after the Databricks ingestion job completes.
          </Alert>
        )}

        {!loading && error && (
          <Alert severity="error" sx={POPPINS}>{error}</Alert>
        )}

        {!loading && status === "ok" && results.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={POPPINS}>
              No similar files found above {(minScore * 100).toFixed(0)}% threshold.
              Try lowering the minimum similarity score.
            </Typography>
          </Box>
        )}

        {!loading && status === "ok" && results.length > 0 && (
          <Grid container spacing={2}>
            {results.map((r, idx) => (
              <Grid item xs={12} sm={6} key={`${r.filename}-${r.user_id}-${idx}`}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    "&:hover": { boxShadow: 3 },
                    transition: "box-shadow 0.2s",
                  }}
                >
                  <CardContent sx={{ pb: "12px !important" }}>
                    {/* File name */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                      <InsertDriveFileOutlinedIcon
                        sx={{ color: "#1976d2", fontSize: 18, mt: 0.2, flexShrink: 0 }}
                      />
                      <Tooltip title={r.filename}>
                        <Typography
                          variant="body2"
                          sx={{
                            ...POPPINS,
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 200,
                          }}
                        >
                          {r.filename}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* Uploader */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                      <PersonOutlineIcon sx={{ fontSize: 14, color: "#9e9e9e" }} />
                      <Typography variant="caption" color="text.secondary" sx={POPPINS}>
                        {r.user_id ?? "Unknown user"}
                      </Typography>
                    </Box>

                    {/* Similarity score */}
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption" sx={{ ...POPPINS, color: "#555" }}>
                          Similarity
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ ...POPPINS, fontWeight: 700, color: scoreColor(r.avg_score) }}
                        >
                          {((r.avg_score ?? 0) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(r.avg_score ?? 0) * 100}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: "#e0e0e0",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: scoreColor(r.avg_score),
                          },
                        }}
                      />
                    </Box>

                    {/* Footer row */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <FileStatusChip status={r.status || "completed"} />
                      {onUseInChat && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => onUseInChat(r)}
                          sx={{
                            ...POPPINS,
                            fontSize: "0.7rem",
                            color: PRIMARY_COLOR,
                            textTransform: "none",
                            p: 0.5,
                          }}
                        >
                          Use in Chat
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

SimilarFilesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  file: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onUseInChat: PropTypes.func,
};

export default SimilarFilesDialog;
